import { useMemo, useEffect } from "react";
import { useIntl } from "react-intl";

import { SalesDataTimePeriod } from "../../../data-store";
import useSalesDataTimePeriods from "../../../data-store/useSalesDataTimePeriods";
import { formatTimePeriod } from "../../../util/timePeriodFormatting";
import Select from "../../utils/Select";

interface TimePeriodSelectorProps {
  selectedPeriod: SalesDataTimePeriod | null;
  onPeriodChange: (period: SalesDataTimePeriod | null) => void;
}

function TimePeriodSelector({
  selectedPeriod,
  onPeriodChange,
}: TimePeriodSelectorProps) {
  // TODO: this should be in a sales directory since it's used in dashboard and sales data
  const intl = useIntl();
  const [timePeriodsStatus] = useSalesDataTimePeriods();

  // Automatically select the most recent time period when data loads
  useEffect(() => {
    if (
      timePeriodsStatus.type === "success" &&
      timePeriodsStatus.value &&
      timePeriodsStatus.value.timePeriods.length > 0 &&
      selectedPeriod === null
    ) {
      // Time periods are sorted with most recent first
      const mostRecentPeriod = timePeriodsStatus.value.timePeriods[0];
      onPeriodChange(mostRecentPeriod);
    }
  }, [timePeriodsStatus, selectedPeriod, onPeriodChange]);

  const availablePeriods = useMemo(() => {
    const periods: Array<{ label: string; value: SalesDataTimePeriod }> = [];

    if (timePeriodsStatus.type === "success" && timePeriodsStatus.value) {
      const periodOptions = timePeriodsStatus.value.timePeriods.map(
        (period: SalesDataTimePeriod) => ({
          label: formatTimePeriod(period, intl),
          value: period,
        })
      );

      periods.push(...periodOptions);
    }

    return periods;
  }, [timePeriodsStatus, intl]);

  return (
    <div style={{ maxWidth: "180px" }}>
      <Select<{ label: string; value: SalesDataTimePeriod }>
        options={availablePeriods}
        renderOption={(option) => option.label}
        optionKey={(option) => option.label}
        value={
          availablePeriods.find((period) => {
            if (!selectedPeriod) return false;
            return period.label === formatTimePeriod(selectedPeriod, intl);
          }) || null
        }
        onChange={(option) => option && onPeriodChange(option.value)}
        isClearable={false}
        dropdownArrow="upDown"
        placeholder={intl.formatMessage({
          id: "components/sales-dashboard/TimePeriodSelector:timePeriodSelectorPlaceholder",
          defaultMessage: "Select a time period...",
        })}
      />
    </div>
  );
}

export default TimePeriodSelector;
